import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  BookOpen,
  Calculator,
  CheckCircle2,
  CircleDollarSign,
  Send,
  ShieldCheck,
  Sparkles,
  UserCheck,
} from "lucide-react";
import {
  approvePayrollRun,
  calculatePayrollRun,
  getPayrollRunDetail,
  getPayrollRuns,
  publishPayrollRun,
  rejectPayrollRun,
  submitPayrollRun,
  updatePayrollPayslip,
} from "../../../api/hrm/payroll/payroll";
import SalaryFilter from "./SalaryFilter";
import SalaryRunCreateModal from "./SalaryRunCreateModal";
import { startSalaryListTour } from "./salaryListTour";
import { showErrorToast, showInputDialog, showSuccessToast } from "../../../components/ConfirmDialog";
import { hasPermission } from "../../../utils/permissionUtils";
import {
  getMockPayrollRunDetail,
  getMockPayrollRuns,
  isPayrollMockForced,
  mockApprovePayrollRun,
  mockCalculatePayrollRun,
  mockPublishPayrollRun,
  mockRejectPayrollRun,
  mockSubmitPayrollRun,
  mockUpdatePayslip,
  shouldUsePayrollMock,
} from "../../hrm/payroll/payrollMockData";

const openGuide = (path) => {
  const base = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  window.open(`${base}${path}`, "_blank", "noopener,noreferrer");
};

const statusLabel = {
  draft: "Nháp",
  calculating: "Đang tính",
  queued: "Sẵn sàng",
  waiting: "Đang chờ",
  pending_coo: "Chờ COO",
  pending_ceo: "Chờ CEO",
  approved: "Đã duyệt",
  rejected: "Bị trả về",
  published: "Đã công bố",
};

const statusClass = {
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  calculating: "bg-blue-50 text-blue-700 border-blue-200",
  queued: "bg-slate-100 text-slate-700 border-slate-200",
  waiting: "bg-amber-50 text-amber-700 border-amber-200",
  pending_coo: "bg-amber-50 text-amber-700 border-amber-200",
  pending_ceo: "bg-orange-50 text-orange-700 border-orange-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-rose-50 text-rose-700 border-rose-200",
  published: "bg-violet-50 text-violet-700 border-violet-200",
};

function formatCurrency(value) {
  return `${Number(value || 0).toLocaleString("vi-VN")} ₫`;
}

function formatMoneyInput(value) {
  if (value === "" || value === null || value === undefined) return "";
  const digits = String(value).replace(/\D/g, "");
  if (!digits) return "";
  return Number(digits).toLocaleString("vi-VN");
}

function parseMoneyInput(raw) {
  const digits = String(raw || "").replace(/\D/g, "");
  return digits === "" ? 0 : Number(digits);
}

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()} ${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

export default function SalaryListPage() {
  const [runs, setRuns] = useState([]);
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [filters, setFilters] = useState({ keyword: "", year_month: "", branch_id: "", status: "" });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editDrafts, setEditDrafts] = useState({});
  const [usingMock, setUsingMock] = useState(false);

  const selectedRunIdRef = useRef(null);
  selectedRunIdRef.current = selectedRunId;
  const usingMockRef = useRef(false);
  usingMockRef.current = usingMock;

  const canCreate = hasPermission("finance.salary", "C");
  const canUpdate = hasPermission("finance.salary", "U") || usingMock;
  const canApprove = hasPermission("finance.salary", "A") || usingMock;

  const applyDetail = (data) => {
    setDetail(data);
    setEditDrafts(
      (data?.payslips || []).reduce((acc, row) => {
        acc[row.id] = {
          adjustment_plus: row.adjustment_plus ?? 0,
          adjustment_minus: row.adjustment_minus ?? 0,
          manual_deduction: row.manual_deduction ?? 0,
          note: row.note || "",
        };
        return acc;
      }, {})
    );
  };

  const fetchRuns = useCallback(async (preferredRunId = null) => {
    try {
      setLoading(true);
      let records = [];
      let mock = usingMockRef.current;

      if (isPayrollMockForced() || mock) {
        records = getMockPayrollRuns({
          year_month: filters.year_month || undefined,
          status: filters.status || undefined,
        });
        mock = true;
      } else {
        try {
          records = await getPayrollRuns({
            ...(filters.year_month ? { year_month: filters.year_month } : {}),
            ...(filters.status ? { status: filters.status } : {}),
            ...(filters.branch_id ? { branch_id: filters.branch_id } : {}),
          });
          if (shouldUsePayrollMock(records)) {
            records = getMockPayrollRuns({
              year_month: filters.year_month || undefined,
              status: filters.status || undefined,
            });
            mock = true;
          }
        } catch (err) {
          console.error(err);
          records = getMockPayrollRuns({
            year_month: filters.year_month || undefined,
            status: filters.status || undefined,
          });
          mock = true;
          showErrorToast("Không tải được API — đang xem dữ liệu mẫu.");
        }
      }

      setUsingMock(mock);
      setRuns(records);
      const currentId = selectedRunIdRef.current;
      const targetId =
        preferredRunId ||
        (records.some((row) => row.id === currentId) ? currentId : records[0]?.id || null);
      setSelectedRunId(targetId || null);
    } finally {
      setLoading(false);
    }
  }, [filters.year_month, filters.status, filters.branch_id]);

  const fetchDetail = useCallback(async (runId) => {
    if (!runId) {
      setDetail(null);
      return;
    }
    try {
      setDetailLoading(true);
      if (usingMockRef.current || String(runId).startsWith("mock-")) {
        const data = getMockPayrollRunDetail(runId);
        applyDetail(data);
        return;
      }
      const data = await getPayrollRunDetail(runId);
      applyDetail(data);
    } catch (err) {
      console.error(err);
      const data = getMockPayrollRunDetail(runId);
      if (data) {
        setUsingMock(true);
        applyDetail(data);
      } else {
        showErrorToast("Không thể tải chi tiết kỳ lương.");
      }
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  useEffect(() => {
    fetchDetail(selectedRunId);
  }, [fetchDetail, selectedRunId]);

  const selectedRun = detail?.run || null;
  const payslips = detail?.payslips || [];
  const summary = detail?.summary || {};
  const approvals = detail?.approvals || [];

  const visibleRuns = useMemo(() => {
    const keyword = filters.keyword.trim().toLowerCase();
    if (!keyword) return runs;
    return runs.filter((row) =>
      [row.year_month, row.run_code, row.branch_name, row.created_by_name, row.current_approver_name]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(keyword))
    );
  }, [runs, filters.keyword]);

  const pendingApprovals = useMemo(
    () => visibleRuns.filter((row) => row.status === "pending_coo" || row.status === "pending_ceo"),
    [visibleRuns]
  );

  const handleCreated = async (runId) => {
    await fetchRuns(runId);
    if (runId) setSelectedRunId(runId);
  };

  const refreshSelected = async (promiseFactory, successMessage) => {
    if (!selectedRunId) return;
    try {
      const res = await promiseFactory();
      showSuccessToast(successMessage);
      applyDetail(res);
      await fetchRuns(selectedRunId);
    } catch (err) {
      showErrorToast(err.response?.data?.message || err.message || "Không thể xử lý thao tác.");
    }
  };

  const runAction = (apiFn, mockFn, successMessage) =>
    refreshSelected(
      () => (usingMock ? Promise.resolve(mockFn(selectedRunId)) : apiFn(selectedRunId)),
      successMessage
    );

  const handleReject = async () => {
    if (!selectedRunId) return;
    const reason = await showInputDialog({
      title: "Từ chối kỳ lương",
      inputLabel: "Nhập lý do từ chối:",
      confirmText: "Từ chối",
    });
    if (!reason) return;
    await refreshSelected(
      () =>
        usingMock
          ? Promise.resolve(mockRejectPayrollRun(selectedRunId, reason))
          : rejectPayrollRun(selectedRunId, reason),
      "Đã trả kỳ lương về Kế toán."
    );
  };

  const handleSavePayslip = async (payslipId) => {
    if (!selectedRunId || !editDrafts[payslipId]) return;
    try {
      const res = usingMock
        ? mockUpdatePayslip(selectedRunId, payslipId, editDrafts[payslipId])
        : await updatePayrollPayslip(selectedRunId, payslipId, editDrafts[payslipId]);
      showSuccessToast("Đã cập nhật phiếu lương.");
      applyDetail(res);
      await fetchRuns(selectedRunId);
    } catch (err) {
      showErrorToast(err.response?.data?.message || err.message || "Không thể cập nhật phiếu lương.");
    }
  };

  return (
    <div className="w-full min-h-screen bg-gray-50/30 p-3 pb-24 md:p-8 md:pb-8">
      <div className="mb-4 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div data-tour="finance-salary-header">
          <div className="mb-1 flex items-center gap-3 sm:mb-2 sm:gap-4">
            <div className="rounded-2xl border border-red-700/10 bg-red-700/5 p-3 text-red-700 shadow-sm sm:rounded-[1.5rem] sm:p-4">
              <CircleDollarSign className="h-5 w-5 sm:h-6 sm:w-6" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl font-black uppercase tracking-tight leading-tight text-gray-800 sm:text-2xl">
                Quản lý <span className="text-red-700">Lương nhân sự</span>
              </h1>
              <p className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400 sm:mt-1 sm:tracking-[0.2em]">
                Chốt công, tính lương, COO duyệt, CEO duyệt và công bố phiếu lương
              </p>
            </div>
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2 self-start sm:self-auto">
          <button
            type="button"
            onClick={() => window.setTimeout(() => startSalaryListTour(), 80)}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-red-200 bg-red-50/90 px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-red-800 shadow-sm transition hover:border-red-300 hover:bg-red-100"
          >
            <Sparkles className="h-4 w-4 text-red-600" strokeWidth={2.25} />
            Production tour
          </button>
          <button
            type="button"
            onClick={() => openGuide("/guides/finance-salary-guide.html")}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-gray-200 bg-white px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-gray-700 shadow-sm transition hover:border-red-300 hover:bg-red-50 hover:text-red-700"
          >
            <BookOpen className="h-4 w-4" strokeWidth={2.25} />
            Documentation
          </button>
        </div>
      </div>

      {usingMock && (
        <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Đang xem <strong>dữ liệu mẫu</strong> — có thể thử tính lương, gửi duyệt, duyệt/từ chối, công bố và lưu điều chỉnh trên trình duyệt (không ghi DB).
          Thêm <code className="rounded bg-amber-100 px-1.5 py-0.5 text-xs">?mock=1</code> để luôn dùng mẫu.
        </div>
      )}

      <div data-tour="finance-salary-filter">
        <SalaryFilter
          onFilterChange={setFilters}
          onNewClick={() => {
            if (usingMock) {
              showErrorToast(
                "Chế độ mẫu: chọn kỳ Nháp / Chờ COO / Chờ CEO / Đã duyệt có sẵn để thử luồng. Tạo kỳ thật cần chốt công trước."
              );
              return;
            }
            setShowCreateModal(true);
          }}
          onRefresh={() => fetchRuns()}
        />
      </div>

      <div className="space-y-6">
        <section className="grid grid-cols-1 xl:grid-cols-[360px,1fr] gap-6">
          <div className="space-y-4">
            <div
              className="bg-white border border-gray-100 rounded-3xl p-4 shadow-sm"
              data-tour="finance-salary-pending"
            >
              <h2 className="text-sm font-black uppercase tracking-widest text-gray-700 mb-3">
                Kỳ lương chờ duyệt
              </h2>
              {pendingApprovals.length === 0 ? (
                <p className="text-sm text-gray-500">Hiện không có kỳ lương nào đang chờ duyệt.</p>
              ) : (
                <div className="space-y-3">
                  {pendingApprovals.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setSelectedRunId(item.id)}
                      className={`w-full text-left rounded-2xl border p-4 transition ${
                        item.id === selectedRunId
                          ? "border-red-200 bg-red-50/60"
                          : "border-gray-100 bg-white hover:border-gray-200"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <strong className="text-sm text-gray-800">{item.year_month}</strong>
                        <span className={`px-2.5 py-1 rounded-full border text-[10px] font-black uppercase ${statusClass[item.status] || statusClass.draft}`}>
                          {statusLabel[item.status] || item.status}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        {item.current_approver_name || "Chưa gán người duyệt"} • {item.payslip_count || 0} nhân viên
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div
              className="bg-white border border-gray-100 rounded-3xl p-4 shadow-sm"
              data-tour="finance-salary-runs"
            >
              <h2 className="text-sm font-black uppercase tracking-widest text-gray-700 mb-3">Danh sách kỳ lương</h2>
              {loading ? (
                <p className="text-sm text-gray-500">Đang tải dữ liệu...</p>
              ) : visibleRuns.length === 0 ? (
                <p className="text-sm text-gray-500">Chưa có kỳ lương phù hợp bộ lọc.</p>
              ) : (
                <div className="space-y-3 max-h-[70vh] overflow-auto pr-1">
                  {visibleRuns.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setSelectedRunId(item.id)}
                      className={`w-full text-left rounded-2xl border p-4 transition ${
                        item.id === selectedRunId
                          ? "border-red-200 bg-red-50/60"
                          : "border-gray-100 bg-white hover:border-gray-200"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-sm font-black text-gray-800">{item.year_month}</div>
                          <div className="text-xs text-gray-500 mt-1">{item.branch_name || "Toàn công ty"}</div>
                        </div>
                        <span className={`px-2.5 py-1 rounded-full border text-[10px] font-black uppercase ${statusClass[item.status] || statusClass.draft}`}>
                          {statusLabel[item.status] || item.status}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 mt-3 text-xs text-gray-600">
                        <span>{item.payslip_count || 0} nhân viên</span>
                        <span className="text-right">{formatCurrency(item.net_total || 0)}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-4" data-tour="finance-salary-detail">
            {!selectedRun ? (
              <div className="bg-white border border-dashed border-gray-200 rounded-3xl p-10 text-center text-gray-500 shadow-sm">
                Chọn một kỳ lương để xem chi tiết và thao tác.
              </div>
            ) : (
              <>
                <section className="bg-white border border-gray-100 rounded-3xl p-5 shadow-sm">
                  <div className="flex flex-col xl:flex-row xl:items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h2 className="text-lg font-black text-gray-800 uppercase tracking-tight">
                          Kỳ lương {selectedRun.year_month}
                        </h2>
                        <span className={`px-3 py-1 rounded-full border text-[10px] font-black uppercase ${statusClass[selectedRun.status] || statusClass.draft}`}>
                          {statusLabel[selectedRun.status] || selectedRun.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        {selectedRun.branch_name || "Toàn công ty"} • Tạo bởi {selectedRun.created_by_name || "—"}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Gửi duyệt: {formatDateTime(selectedRun.submitted_at)} • Công bố: {formatDateTime(selectedRun.published_at)}
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {canUpdate && (
                        <>
                          <button
                            type="button"
                            onClick={() =>
                              runAction(calculatePayrollRun, mockCalculatePayrollRun, "Đã tính lại kỳ lương.")
                            }
                            className="px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-semibold text-gray-700 flex items-center gap-2"
                          >
                            <Calculator size={16} />
                            Tính lương
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              runAction(submitPayrollRun, mockSubmitPayrollRun, "Đã gửi COO duyệt.")
                            }
                            className="px-4 py-2.5 bg-slate-900 text-white rounded-xl text-sm font-bold flex items-center gap-2"
                          >
                            <Send size={16} />
                            Gửi duyệt
                          </button>
                        </>
                      )}
                      {canApprove && (selectedRun.status === "pending_coo" || selectedRun.status === "pending_ceo") && (
                        <>
                          <button
                            type="button"
                            onClick={() =>
                              runAction(approvePayrollRun, mockApprovePayrollRun, "Đã duyệt kỳ lương.")
                            }
                            className="px-4 py-2.5 bg-emerald-600 text-white rounded-xl text-sm font-bold flex items-center gap-2"
                          >
                            <CheckCircle2 size={16} />
                            Duyệt
                          </button>
                          <button
                            type="button"
                            onClick={handleReject}
                            className="px-4 py-2.5 bg-white border border-rose-200 text-rose-700 rounded-xl text-sm font-bold"
                          >
                            Từ chối
                          </button>
                        </>
                      )}
                      {canApprove && selectedRun.status === "approved" && (
                        <button
                          type="button"
                          onClick={() =>
                            runAction(
                              publishPayrollRun,
                              mockPublishPayrollRun,
                              "Đã công bố phiếu lương cho nhân viên."
                            )
                          }
                          className="px-4 py-2.5 bg-violet-600 text-white rounded-xl text-sm font-bold flex items-center gap-2"
                        >
                          <UserCheck size={16} />
                          Công bố
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-5">
                    <div className="rounded-2xl border border-gray-100 bg-slate-50 p-4">
                      <div className="text-xs uppercase font-black tracking-widest text-gray-500">Số nhân viên</div>
                      <div className="text-2xl font-black text-gray-800 mt-2">{summary.employee_count || 0}</div>
                    </div>
                    <div className="rounded-2xl border border-gray-100 bg-emerald-50 p-4">
                      <div className="text-xs uppercase font-black tracking-widest text-emerald-600">Tổng gross</div>
                      <div className="text-lg font-black text-emerald-800 mt-2">{formatCurrency(summary.gross_total || 0)}</div>
                    </div>
                    <div className="rounded-2xl border border-gray-100 bg-blue-50 p-4">
                      <div className="text-xs uppercase font-black tracking-widest text-blue-600">Tổng thực lĩnh</div>
                      <div className="text-lg font-black text-blue-800 mt-2">{formatCurrency(summary.net_total || 0)}</div>
                    </div>
                    <div className="rounded-2xl border border-gray-100 bg-amber-50 p-4">
                      <div className="text-xs uppercase font-black tracking-widest text-amber-600">Tổng tăng ca</div>
                      <div className="text-lg font-black text-amber-800 mt-2">{formatCurrency(summary.overtime_total || 0)}</div>
                    </div>
                  </div>
                </section>

                <section className="bg-white border border-gray-100 rounded-3xl p-5 shadow-sm">
                  <div className="flex items-center gap-2 mb-4">
                    <ShieldCheck size={18} className="text-red-700" />
                    <h3 className="text-sm font-black uppercase tracking-widest text-gray-700">Tiến trình duyệt</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {approvals.map((approval) => (
                      <div key={approval.id} className="rounded-2xl border border-gray-100 p-4 bg-slate-50">
                        <div className="flex items-center justify-between gap-3">
                          <strong className="text-sm text-gray-800">
                            Cấp {approval.level}: {approval.approver_role}
                          </strong>
                          <span className={`px-2.5 py-1 rounded-full border text-[10px] font-black uppercase ${statusClass[approval.status] || statusClass.draft}`}>
                            {statusLabel[approval.status] || approval.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-2">{approval.approver_name || "Chưa gán"}</p>
                        <p className="text-xs text-gray-500 mt-1">Thao tác lúc: {formatDateTime(approval.acted_at)}</p>
                        {approval.note && <p className="text-xs text-rose-600 mt-2">Ghi chú: {approval.note}</p>}
                      </div>
                    ))}
                  </div>
                </section>

                <section className="bg-white border border-gray-100 rounded-3xl p-5 shadow-sm">
                  <div className="flex items-center gap-2 mb-4">
                    <CircleDollarSign size={18} className="text-red-700" />
                    <h3 className="text-sm font-black uppercase tracking-widest text-gray-700">Bảng lương chi tiết</h3>
                  </div>

                  {detailLoading ? (
                    <p className="text-sm text-gray-500">Đang tải chi tiết kỳ lương...</p>
                  ) : payslips.length === 0 ? (
                    <p className="text-sm text-gray-500">Kỳ lương chưa có dữ liệu. Hãy bấm “Tính lương”.</p>
                  ) : (
                    <div className="space-y-4">
                      {payslips.map((row) => {
                        const draft = editDrafts[row.id] || {
                          adjustment_plus: 0,
                          adjustment_minus: 0,
                          manual_deduction: 0,
                          note: "",
                        };
                        return (
                          <div key={row.id} className="rounded-3xl border border-gray-100 p-4 md:p-5">
                            <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                              <div>
                                <h4 className="text-base font-black text-gray-800 uppercase">{row.employee_name_snapshot}</h4>
                                <p className="text-sm text-gray-500 mt-1">
                                  {row.department_name_snapshot || "—"} • {row.position_snapshot || "—"}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  Ngân hàng: {row.payroll_bank_name_snapshot || "—"} / {row.payroll_bank_number_snapshot || "—"}
                                </p>
                              </div>
                              <div className="text-right">
                                <div className="text-xs uppercase font-black tracking-widest text-gray-500">Thực lĩnh</div>
                                <div className="text-xl font-black text-red-700 mt-1">{formatCurrency(row.net_pay)}</div>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4 text-sm">
                              <div className="rounded-2xl bg-slate-50 border border-slate-100 p-3">
                                <div className="text-xs uppercase font-black text-slate-500">Lương cơ sở</div>
                                <div className="font-bold text-slate-800 mt-1">{formatCurrency(row.base_salary)}</div>
                              </div>
                              <div className="rounded-2xl bg-slate-50 border border-slate-100 p-3">
                                <div className="text-xs uppercase font-black text-slate-500">Phụ cấp</div>
                                <div className="font-bold text-slate-800 mt-1">{formatCurrency(row.allowance)}</div>
                              </div>
                              <div className="rounded-2xl bg-slate-50 border border-slate-100 p-3">
                                <div className="text-xs uppercase font-black text-slate-500">Ngày công</div>
                                <div className="font-bold text-slate-800 mt-1">
                                  {Number(row.actual_work_days || 0)} / {Number(row.standard_work_days || 0)}
                                </div>
                              </div>
                              <div className="rounded-2xl bg-slate-50 border border-slate-100 p-3">
                                <div className="text-xs uppercase font-black text-slate-500">Tăng ca</div>
                                <div className="font-bold text-slate-800 mt-1">
                                  {formatCurrency(row.overtime_amount)} ({Number(row.overtime_minutes || 0) / 60} giờ)
                                </div>
                              </div>
                              <div className="rounded-2xl bg-amber-50 border border-amber-100 p-3">
                                <div className="text-xs uppercase font-black text-amber-700">BHXH + BHYT + BHTN</div>
                                <div className="font-bold text-amber-950 mt-1">
                                  {formatCurrency(row.insurance_total_employee)}
                                </div>
                                <div className="text-[10px] text-amber-800/80 mt-1">
                                  {formatCurrency(row.bhxh_employee)} / {formatCurrency(row.bhyt_employee)} /{" "}
                                  {formatCurrency(row.bhtn_employee)}
                                </div>
                              </div>
                              <div className="rounded-2xl bg-violet-50 border border-violet-100 p-3">
                                <div className="text-xs uppercase font-black text-violet-700">Thuế TNCN</div>
                                <div className="font-bold text-violet-950 mt-1">
                                  {formatCurrency(row.pit_amount)}
                                </div>
                                <div className="text-[10px] text-violet-800/80 mt-1">
                                  Tính thuế: {formatCurrency(row.taxable_income)} · PT:{" "}
                                  {Number(row.tax_dependent_count || 0)}
                                </div>
                              </div>
                              <div className="rounded-2xl bg-slate-50 border border-slate-100 p-3">
                                <div className="text-xs uppercase font-black text-slate-500">Tổng thu nhập</div>
                                <div className="font-bold text-slate-800 mt-1">{formatCurrency(row.gross_pay)}</div>
                              </div>
                              <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-3">
                                <div className="text-xs uppercase font-black text-emerald-700">Thực lĩnh</div>
                                <div className="font-bold text-emerald-900 mt-1">{formatCurrency(row.net_pay)}</div>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
                              <label className="text-sm text-gray-700">
                                <span className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-1">Điều chỉnh cộng</span>
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  value={formatMoneyInput(draft.adjustment_plus)}
                                  disabled={!canUpdate}
                                  onChange={(e) =>
                                    setEditDrafts((prev) => ({
                                      ...prev,
                                      [row.id]: {
                                        ...prev[row.id],
                                        adjustment_plus: parseMoneyInput(e.target.value),
                                      },
                                    }))
                                  }
                                  className="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white tabular-nums"
                                  placeholder="0"
                                />
                              </label>
                              <label className="text-sm text-gray-700">
                                <span className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-1">Điều chỉnh trừ</span>
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  value={formatMoneyInput(draft.adjustment_minus)}
                                  disabled={!canUpdate}
                                  onChange={(e) =>
                                    setEditDrafts((prev) => ({
                                      ...prev,
                                      [row.id]: {
                                        ...prev[row.id],
                                        adjustment_minus: parseMoneyInput(e.target.value),
                                      },
                                    }))
                                  }
                                  className="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white tabular-nums"
                                  placeholder="0"
                                />
                              </label>
                              <label className="text-sm text-gray-700">
                                <span className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-1">Khấu trừ thủ công</span>
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  value={formatMoneyInput(draft.manual_deduction)}
                                  disabled={!canUpdate}
                                  onChange={(e) =>
                                    setEditDrafts((prev) => ({
                                      ...prev,
                                      [row.id]: {
                                        ...prev[row.id],
                                        manual_deduction: parseMoneyInput(e.target.value),
                                      },
                                    }))
                                  }
                                  className="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white tabular-nums"
                                  placeholder="0"
                                />
                              </label>
                              <label className="text-sm text-gray-700">
                                <span className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-1">Ghi chú</span>
                                <input
                                  type="text"
                                  value={draft.note}
                                  disabled={!canUpdate}
                                  onChange={(e) =>
                                    setEditDrafts((prev) => ({
                                      ...prev,
                                      [row.id]: { ...prev[row.id], note: e.target.value },
                                    }))
                                  }
                                  className="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white"
                                />
                              </label>
                            </div>

                            <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4 mt-4">
                              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                                {(row.items || []).map((item) => (
                                  <div key={item.id} className="flex items-center justify-between bg-slate-50 rounded-2xl px-3 py-2 border border-slate-100">
                                    <span className="text-gray-600">{item.label}</span>
                                    <strong className="text-gray-800">{item.amount ? formatCurrency(item.amount) : "—"}</strong>
                                  </div>
                                ))}
                              </div>

                              {canUpdate && (
                                <button
                                  type="button"
                                  onClick={() => handleSavePayslip(row.id)}
                                  className="px-4 py-2.5 bg-red-700 text-white rounded-xl text-sm font-bold self-start"
                                >
                                  Lưu điều chỉnh
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>
              </>
            )}
          </div>
        </section>
      </div>

      {canCreate && (
        <SalaryRunCreateModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
}
